import React from "react";
import { Button, Text, HStack, Image, Spinner } from "@chakra-ui/react";
import { RepeatIcon, ArrowForwardIcon } from "@chakra-ui/icons";
import LoadingIcon from "@root/assets/images/loading-icon.svg";
import {
  ACTION_CONSTANTS,
  ASSESSMENT_STATUS,
} from "@root/components/assessments/actionConstants";

interface BaseAssessment {
  id: string;
  status:
    | "pending"
    | "completed"
    | "in_progress"
    | "processing_sources"
    | "failed"
    | "irrelevant_doc";
}

interface RenderActionButtonProps<T extends BaseAssessment> {
  assessment: T;
  onStartAnalysis: (assessment: T) => void;
  onViewOutput: (assessment: T) => void;
  onRetry: (assessment: T) => void;
  t: (key: string) => string;
}

export const renderActionButton = <T extends BaseAssessment>({
  assessment,
  onStartAnalysis,
  onViewOutput,
  onRetry,
  t,
}: RenderActionButtonProps<T>): React.ReactNode => {
  switch (assessment.status) {
    case ASSESSMENT_STATUS.PENDING:
      return (
        <Button
          width={ACTION_CONSTANTS.pending.width}
          variant={ACTION_CONSTANTS.pending.variant}
          size={ACTION_CONSTANTS.pending.size}
          border={ACTION_CONSTANTS.pending.border}
          color={ACTION_CONSTANTS.pending.color}
          background={ACTION_CONSTANTS.pending.background}
          rightIcon={<ArrowForwardIcon boxSize={4} />}
          onClick={() => onStartAnalysis(assessment)}
          _hover={{
            bg: ACTION_CONSTANTS.pending.hoverBg,
            color: ACTION_CONSTANTS.pending.hoverColor,
          }}
        >
          {t(ACTION_CONSTANTS.pending.translationKey)}
        </Button>
      );
    case ASSESSMENT_STATUS.COMPLETED:
      return (
        <Button
          width={ACTION_CONSTANTS.completed.width}
          variant={ACTION_CONSTANTS.completed.variant}
          size={ACTION_CONSTANTS.completed.size}
          border={ACTION_CONSTANTS.completed.border}
          color={ACTION_CONSTANTS.completed.color}
          rightIcon={<ArrowForwardIcon boxSize={4} />}
          onClick={() => onViewOutput(assessment)}
          _hover={{
            bg: ACTION_CONSTANTS.completed.hoverBg,
            color: ACTION_CONSTANTS.completed.hoverColor,
          }}
        >
          {t(ACTION_CONSTANTS.completed.translationKey)}
        </Button>
      );
    case ASSESSMENT_STATUS.IN_PROGRESS:
      return (
        <HStack spacing={2} w={"150px"}>
          <Image
            src={LoadingIcon}
            filter="brightness(0) saturate(100%) invert(63%) sepia(87%) saturate(459%) hue-rotate(80deg) brightness(96%) contrast(87%)"
          />
          <Text fontSize="sm" color={ACTION_CONSTANTS.inProgress.color}>
            {t(ACTION_CONSTANTS.inProgress.translationKey)}
          </Text>
        </HStack>
      );
    case ASSESSMENT_STATUS.PROCESSING_SOURCES:
      return (
        <Button
          width={ACTION_CONSTANTS.processingSources.width}
          variant={ACTION_CONSTANTS.processingSources.variant}
          size={ACTION_CONSTANTS.processingSources.size}
          border={ACTION_CONSTANTS.processingSources.border}
          color={ACTION_CONSTANTS.processingSources.color}
          onClick={() => onViewOutput(assessment)}
          leftIcon={
            <Spinner
              size="sm"
              color={ACTION_CONSTANTS.processingSources.color}
              thickness="2px"
            />
          }
          _hover={{
            bg: ACTION_CONSTANTS.processingSources.hoverBg,
            color: ACTION_CONSTANTS.processingSources.hoverColor,
          }}
        >
          {t(ACTION_CONSTANTS.processingSources.translationKey)}
        </Button>
      );
    case ASSESSMENT_STATUS.FAILED:
      return (
        <Button
          width={ACTION_CONSTANTS.failed.width}
          variant={ACTION_CONSTANTS.failed.variant}
          size={ACTION_CONSTANTS.failed.size}
          border={ACTION_CONSTANTS.failed.border}
          color={ACTION_CONSTANTS.failed.color}
          rightIcon={<RepeatIcon />}
          onClick={() => onRetry(assessment)}
          _hover={{
            bg: ACTION_CONSTANTS.failed.hoverBg,
            color: ACTION_CONSTANTS.failed.hoverColor,
          }}
        >
          {t(ACTION_CONSTANTS.failed.translationKey)}
        </Button>
      );
    case ASSESSMENT_STATUS.IRRELEVANT_DOC:
      return (
        <Button
          width={ACTION_CONSTANTS.irrelevantDoc.width}
          variant={ACTION_CONSTANTS.irrelevantDoc.variant}
          size={ACTION_CONSTANTS.irrelevantDoc.size}
          border={ACTION_CONSTANTS.irrelevantDoc.border}
          color={ACTION_CONSTANTS.irrelevantDoc.color}
          cursor="not-allowed"
          _hover={{
            bg: ACTION_CONSTANTS.irrelevantDoc.hoverBg,
            color: ACTION_CONSTANTS.irrelevantDoc.hoverColor,
          }}
        >
          {t(ACTION_CONSTANTS.irrelevantDoc.translationKey)}
        </Button>
      );
    default:
      return null;
  }
};
